import React, { useState, useEffect } from 'react';
import { portfolioFinancials } from './portfolioFinancials';
import {
  AreaChart, Area, BarChart, Bar, LineChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, Treemap
} from 'recharts';

console.log('🔧 Recharts Tooltip component:', Tooltip);
import {
  DollarSign, TrendingUp, TrendingDown,
  BarChart3, PieChartIcon, AlertTriangle,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';

// Normalize industry names to standard 10 verticals
const normalizeIndustry = (industry) => {
  if (!industry) return 'Technology'; // Default to Technology instead of 'Other'

  const normalized = industry.toLowerCase().trim();

  // Standard 10 industry verticals mapping
  const industryMap = {
    // Technology variations
    'technology': 'Technology',
    'tech': 'Technology',
    'it': 'Technology',
    'information technology': 'Technology',
    'software': 'Technology',
    'professional services': 'Technology',
    'consulting': 'Technology',
    'services': 'Technology',

    // Healthcare variations
    'healthcare': 'Healthcare',
    'health care': 'Healthcare',
    'medical': 'Healthcare',
    'pharmaceutical': 'Healthcare',
    'pharma': 'Healthcare',

    // Finance variations
    'finance': 'Finance',
    'financial': 'Finance',
    'financial services': 'Finance',
    'banking': 'Finance',
    'insurance': 'Finance',

    // Retail variations
    'retail': 'Retail',
    'e-commerce': 'Retail',
    'ecommerce': 'Retail',

    // Manufacturing variations
    'manufacturing': 'Manufacturing',
    'industrial': 'Manufacturing',

    // Energy variations
    'energy': 'Energy',
    'oil': 'Energy',
    'gas': 'Energy',
    'utilities': 'Energy',
    'utility': 'Energy',

    // Transportation and Logistics variations
    'transportation': 'Transportation and Logistics',
    'logistics': 'Transportation and Logistics',
    'transportation and logistics': 'Transportation and Logistics',
    'shipping': 'Transportation and Logistics',

    // Real Estate variations
    'real estate': 'Real Estate',
    'realestate': 'Real Estate',
    'property': 'Real Estate',

    // Hospitality and Tourism variations
    'hospitality': 'Hospitality and Tourism',
    'hospitality and tourism': 'Hospitality and Tourism',
    'tourism': 'Hospitality and Tourism',
    'hotel': 'Hospitality and Tourism',
    'travel': 'Hospitality and Tourism',

    // Education variations
    'education': 'Education',
    'educational': 'Education',
    'university': 'Education',
    'academic': 'Education'
  };

  // Return mapped industry or default to Technology
  return industryMap[normalized] || 'Technology';
};

const FinancialImpactAnalysis = ({ companies = [], portfolioId }) => {
  const [revenueAtRisk, setRevenueAtRisk] = useState(0);
  const [exposureAnalysis, setExposureAnalysis] = useState({});
  const [selectedView, setSelectedView] = useState('revenue');
  const [timeHorizon, setTimeHorizon] = useState('30d');
  const [comparisonMode, setComparisonMode] = useState(false);

  // ⚠ HOOKS BEFORE EARLY RETURNS. The "no companies yet" screen used to return here, above the two
  // useEffect calls below, so the number of hooks changed with the company list: a render with no
  // companies ran 6 hooks and one with companies ran 8. React throws when that count changes
  // between renders of a mounted component — e.g. the Financial tab open while the portfolio's
  // company list goes from empty to loaded, or to a portfolio with none. The loading screen is now
  // returned just before the main render, after every hook. Same class as InvestmentOptimization
  // (see CLAUDE.md "Hooks before early returns"); guarded by FinancialImpactAnalysis.hooks.test.tsx.
  const hasCompanies = Array.isArray(companies) && companies.length > 0;

  // Define formatCurrency before using it
  const formatCurrency = (value) => {
    // Absent is "Not measured", never $0 (portfolioFinancials sends null when nothing is measured).
    if (value === null || value === undefined) return 'Not measured';
    const numValue = parseFloat(value) || 0;
    
    if (numValue >= 1000000000) {
      return `$${(numValue / 1000000000).toFixed(2)}B`;
    } else if (numValue >= 1000000) {
      return `$${(numValue / 1000000).toFixed(2)}M`;
    } else if (numValue >= 1000) {
      return `$${(numValue / 1000).toFixed(1)}K`;
    }
    return `$${numValue.toFixed(0)}`;
  };

  // Clients with the backend's business intelligence: finished assessments only (the backend sends
  // null figures otherwise). Nothing is estimated for the rest.
  const financials = portfolioFinancials(hasCompanies ? companies : [], normalizeIndustry);
  const completedCompanies = (hasCompanies ? companies : []).filter(
    (c) => c.revenue_at_risk !== null && c.revenue_at_risk !== undefined
      && c.recovery_days !== null && c.recovery_days !== undefined
  );
  const finishedCount = (hasCompanies ? companies : []).filter((c) => c.status === 'completed').length;
  const unfinishedCount = (hasCompanies ? companies.length : 0) - finishedCount;
  const finishedWithoutFiguresCount = finishedCount - completedCompanies.length;

  useEffect(() => {
    // Nothing to calculate yet. The effect now runs on the loading render too (it used to be
    // skipped by the early return), so it has to decline the empty case itself.
    if (!hasCompanies) return;
    console.log(`📊 Financial Impact Analysis: Using ${completedCompanies.length} completed assessments out of ${companies.length} total companies`);
    calculateRevenueAtRisk();
    analyzeExposure();
  }, [companies, completedCompanies.length]);

  // Revenue at risk, recovery days and exposure are the backend's per-client business intelligence
  // (Pat, 2026-10-19, option A) — see portfolioFinancials. This used to compute them here from the
  // risk score with a tier table, call revenue x CRS / 100 "exposure", and assign a random
  // geography to any client without one.
  const calculateRevenueAtRisk = () => {
    setRevenueAtRisk(financials.revenueAtRisk);
  };

  const analyzeExposure = () => {
    setExposureAnalysis(financials.exposure);
  };

  const RevenueAtRiskView = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
          <div className="flex items-center justify-between mb-2">
            <DollarSign aria-hidden="true" className="w-8 h-8 text-red-500" />
            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">At Risk</span>
          </div>
          <h3 className="text-2xl font-bold text-[#e6f1ff]">
            {formatCurrency(revenueAtRisk.total)}
          </h3>
          <p className="text-sm text-[#a8b2d1] mt-1">Total Revenue at Risk</p>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 bg-[#1e3148] rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full"
                style={{ width: `${Math.min(100, revenueAtRisk.percentage || 0)}%` }}
              />
            </div>
            <span className="text-xs font-medium">
              {revenueAtRisk.percentage === null || revenueAtRisk.percentage === undefined ? '—' : `${revenueAtRisk.percentage.toFixed(1)}%`}
            </span>
          </div>
        </div>

        <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 aria-hidden="true" className="w-8 h-8 text-blue-500" />
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Total</span>
          </div>
          <h3 className="text-2xl font-bold text-[#e6f1ff]">
            {formatCurrency(revenueAtRisk.totalRevenue)}
          </h3>
          <p className="text-sm text-[#a8b2d1] mt-1">Total Annual Revenue</p>
          <div className="mt-3 text-xs text-[#8892b0]">
            Across {completedCompanies.length} clients with figures
          </div>
        </div>

      </div>

      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h3 className="text-lg font-semibold mb-4">Revenue at Risk by Category</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={Object.entries(revenueAtRisk.byCategory || {}).map(([key, value]) => ({
            category: key,
            atRisk: value.atRisk,
            revenue: value.revenue,
            companies: value.companies
          }))}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis tickFormatter={formatCurrency} />
            <Tooltip formatter={formatCurrency} />
            <Legend />
            <Bar dataKey="atRisk" fill="#ef4444" name="Revenue at Risk" />
            <Bar dataKey="revenue" fill="#3b82f6" name="Total Revenue" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h3 className="text-lg font-semibold mb-4">Top 10 Companies by Revenue at Risk</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 text-sm font-medium">Company</th>
                <th className="text-left py-2 px-3 text-sm font-medium">Industry</th>
                <th className="text-right py-2 px-3 text-sm font-medium">Risk Score</th>
                <th className="text-right py-2 px-3 text-sm font-medium">Recovery Days</th>
                <th className="text-right py-2 px-3 text-sm font-medium">Daily Revenue</th>
                <th className="text-right py-2 px-3 text-sm font-medium">Revenue at Risk</th>
              </tr>
            </thead>
            <tbody>
              {revenueAtRisk.recoveryTimeData?.map((company, index) => (
                <tr key={index} className="border-b hover:bg-[rgba(255,255,255,0.03)]">
                  <td className="py-2 px-3 text-sm font-medium">{company.company}</td>
                  <td className="py-2 px-3 text-sm text-[#a8b2d1]">{company.industry}</td>
                  <td className="text-right py-2 px-3">
                    {company.riskScore === null ? (
                      <span className="text-xs text-[#8892b0]">—</span>
                    ) : (
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        company.riskScore >= 70 ? 'bg-red-100 text-red-700' :
                        company.riskScore >= 50 ? 'bg-orange-100 text-orange-700' :
                        company.riskScore >= 30 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {Math.round(company.riskScore)}
                      </span>
                    )}
                  </td>
                  <td className="text-right py-2 px-3 text-sm">{company.recoveryDays}</td>
                  <td className="text-right py-2 px-3 text-sm">
                    {company.dailyRevenue === null ? '—' : formatCurrency(company.dailyRevenue)}
                  </td>
                  <td className="text-right py-2 px-3 text-sm font-semibold text-red-600">
                    {formatCurrency(company.revenueAtRisk)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const ExposureAnalysisView = () => (
    <div className="space-y-6">
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Total Financial Exposure</h3>
          <AlertTriangle aria-hidden="true" className="w-6 h-6 text-orange-500" />
        </div>
        <div className="text-3xl font-bold text-orange-600 mb-4">
          {formatCurrency(exposureAnalysis.total)}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-[#ccd6f6] mb-3">By Industry</h4>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={Object.entries(exposureAnalysis.byIndustry || {}).map(([key, value]) => ({
                    name: key,
                    value: value.exposure
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {Object.entries(exposureAnalysis.byIndustry || {}).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#0088FE', '#00C49F', '#FFBB28', '#FF8042'][index % 4]} />
                  ))}
                </Pie>
                <Tooltip formatter={formatCurrency} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div>
            <h4 className="font-medium text-[#ccd6f6] mb-3">By Company Size</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={Object.entries(exposureAnalysis.bySize || {}).map(([key, value]) => ({
                size: key,
                exposure: value.exposure,
                companies: value.companies
              }))}>
                <XAxis dataKey="size" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={formatCurrency} />
                <Bar dataKey="exposure" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h3 className="text-lg font-semibold mb-4">Industry Risk Exposure Details</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 text-sm font-medium">Industry</th>
                <th className="text-right py-2 px-3 text-sm font-medium">Companies</th>
                <th className="text-right py-2 px-3 text-sm font-medium">Avg Risk</th>
                <th className="text-right py-2 px-3 text-sm font-medium">Total Revenue</th>
                <th className="text-right py-2 px-3 text-sm font-medium">Exposure</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(exposureAnalysis.byIndustry || {}).map(([industry, data]) => (
                <tr key={industry} className="border-b hover:bg-[rgba(255,255,255,0.03)]">
                  <td className="py-2 px-3 text-sm font-medium">{industry}</td>
                  <td className="text-right py-2 px-3 text-sm">{data.companies}</td>
                  <td className="text-right py-2 px-3">
                    {data.avgRisk === null ? (
                      <span className="text-xs text-[#8892b0]">—</span>
                    ) : (
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        data.avgRisk >= 70 ? 'bg-red-100 text-red-700' :
                        data.avgRisk >= 50 ? 'bg-orange-100 text-orange-700' :
                        data.avgRisk >= 30 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {data.avgRisk.toFixed(1)}
                      </span>
                    )}
                  </td>
                  <td className="text-right py-2 px-3 text-sm">{formatCurrency(data.totalRevenue)}</td>
                  <td className="text-right py-2 px-3 text-sm font-semibold text-orange-600">
                    {formatCurrency(data.exposure)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // No companies yet: every hook above has already run, so returning here keeps the hook count
  // the same on every render.
  if (!hasCompanies) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle aria-hidden="true" className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Loading Data:</strong> Waiting for portfolio company data to load.
                If this persists, please refresh the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Assessment Coverage Info */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <AlertTriangle aria-hidden="true" className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Analysis Coverage:</strong> This financial impact analysis is computed based on{' '}
              <strong>{completedCompanies.length} companies with figures</strong> out of{' '}
              {companies.length} total companies in your portfolio.{' '}
              {/* Two different reasons a client has no figures; the old copy told both to "complete" it. */}
              {unfinishedCount > 0 && (
                <span className="text-blue-600">
                  {unfinishedCount} still finishing their assessment.{' '}
                </span>
              )}
              {finishedWithoutFiguresCount > 0 && (
                <span className="text-blue-600">
                  {finishedWithoutFiguresCount} finished, but their figures could not be calculated (revenue or headcount not answered).
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mb-4">
        <button
          onClick={() => setSelectedView('revenue')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedView === 'revenue' 
              ? 'bg-blue-600 text-white' 
              : 'bg-[#172a45] text-[#ccd6f6] hover:bg-[#1e3148]'
          }`}
        >
          Revenue at Risk
        </button>
        <button
          onClick={() => setSelectedView('exposure')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedView === 'exposure' 
              ? 'bg-blue-600 text-white' 
              : 'bg-[#172a45] text-[#ccd6f6] hover:bg-[#1e3148]'
          }`}
        >
          Exposure Analysis
        </button>
      </div>

      {selectedView === 'revenue' && <RevenueAtRiskView />}
      {selectedView === 'exposure' && <ExposureAnalysisView />}
    </div>
  );
};

export default FinancialImpactAnalysis;